import React, { useState, useEffect } from 'react';
import { Item, Stats } from '../lib/schema';
import { formatPrice } from '../lib/pricing';
import { DirectusService } from '../services/DirectusService';
import UploadTwoImages from './UploadTwoImages';
import ResultCard from './ResultCard';
import BuyForm from './BuyForm';
import SalesTable from './SalesTable';
import ServerStatus from './ServerStatus';

const VendorDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'buy' | 'list' | 'history'>('dashboard');
  const [items, setItems] = useState<Item[]>([]);
  const [stats, setStats] = useState<Stats>({
    revenue: 0,
    cost: 0,
    profit: 0,
    soldCount: 0,
    avgMargin: 0
  });

  // Load data from local server
  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      const serverItems = await DirectusService.getAllCards();
      if (serverItems.length > 0) {
        setItems(serverItems);
      } else {
        // Fallback to localStorage if server is not available
        const savedItems = localStorage.getItem('pokemon-vendor-items');
        if (savedItems) {
          setItems(JSON.parse(savedItems));
        }
      }
    } catch (error) {
      console.error('Failed to load from server, using localStorage:', error);
      const savedItems = localStorage.getItem('pokemon-vendor-items');
      if (savedItems) {
        setItems(JSON.parse(savedItems));
      }
    }
  };

  const calculateStats = () => {
    const soldItems = items.filter(item => item.sold);
    const revenue = soldItems.reduce((sum, item) => sum + (item.sold?.sellPrice || 0), 0);
    const cost = soldItems.reduce((sum, item) => sum + item.buyPrice, 0);
    const profit = revenue - cost;
    const avgMargin = revenue > 0 ? (profit / revenue) * 100 : 0;

    setStats({
      revenue,
      cost,
      profit,
      soldCount: soldItems.length,
      avgMargin
    });
  };

  const handleItemAdded = async (newItem: Item) => {
    try {
      // Try to save to server first
      const savedItem = await DirectusService.addCard(newItem);
      const updatedItems = [...items, savedItem];
      setItems(updatedItems);
      calculateStats();
    } catch (error) {
      console.error('Failed to save to server, using localStorage:', error);
      // Fallback to localStorage
      const updatedItems = [...items, newItem];
      setItems(updatedItems);
      localStorage.setItem('pokemon-vendor-items', JSON.stringify(updatedItems));
      calculateStats();
    }
  };

  const handleItemUpdated = async (updatedItem: Item) => {
    try {
      // Try to update on server first
      const savedItem = await DirectusService.updateCard(updatedItem.id, updatedItem);
      const updatedItems = items.map(item => 
        item.id === updatedItem.id ? savedItem : item
      );
      setItems(updatedItems);
      calculateStats();
    } catch (error) {
      console.error('Failed to update on server, using localStorage:', error);
      // Fallback to localStorage
      const updatedItems = items.map(item => 
        item.id === updatedItem.id ? updatedItem : item
      );
      setItems(updatedItems);
      localStorage.setItem('pokemon-vendor-items', JSON.stringify(updatedItems));
      calculateStats();
    }
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="glass-effect p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Revenue</p>
              <p className="text-2xl font-bold text-white">{formatPrice(stats.revenue)}</p>
            </div>
            <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
          </div>
        </div>

        <div className="glass-effect p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Profit</p>
              <p className={`text-2xl font-bold ${stats.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {formatPrice(stats.profit)}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="glass-effect p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Items Sold</p>
              <p className="text-2xl font-bold text-white">{stats.soldCount}</p>
            </div>
            <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="glass-effect p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Avg Margin</p>
              <p className={`text-2xl font-bold ${stats.avgMargin >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {stats.avgMargin.toFixed(1)}%
              </p>
            </div>
            <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Items */}
      <div className="glass-effect p-6">
        <h3 className="text-xl font-bold text-white mb-4">Recent Items</h3>
        <div className="space-y-3">
          {items.slice(-5).reverse().map(item => (
            <div key={item.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-pokemon-purple/20 rounded-lg flex items-center justify-center">
                  <span className="text-pokemon-purple font-bold text-sm">
                    {item.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="text-white font-medium">{item.name}</p>
                  <p className="text-gray-400 text-sm">{item.set} • {item.condition}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-white font-medium">{formatPrice(item.buyPrice)}</p>
                {item.sold && (
                  <p className="text-green-400 text-sm">{formatPrice(item.sold.sellPrice)}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderBuy = () => (
    <div className="space-y-6">
      <UploadTwoImages onResult={handleItemAdded} />
    </div>
  );

  const renderList = () => (
    <div className="space-y-6">
      <div className="glass-effect p-6">
        <h3 className="text-xl font-bold text-white mb-4">All Items</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(item => (
            <ResultCard 
              key={item.id} 
              item={item} 
              onUpdate={handleItemUpdated}
            />
          ))}
        </div>
      </div>
    </div>
  );

  const renderHistory = () => (
    <div className="space-y-6">
      <SalesTable items={items.filter(item => item.sold)} />
    </div>
  );

  return (
    <div className="min-h-screen pokemon-bg">
      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 sidebar min-h-screen p-6">
          <div className="flex items-center space-x-3 mb-8">
            <div className="w-10 h-10 bg-pokemon-purple rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">⚡</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">PokemonBiz</h1>
              <p className="text-gray-400 text-sm">Vendor Dashboard</p>
            </div>
          </div>

          <nav className="space-y-2">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: '📊' },
              { id: 'buy', label: 'Buy Cards', icon: '🛒' },
              { id: 'list', label: 'Inventory', icon: '📦' },
              { id: 'history', label: 'Sales History', icon: '📈' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-pokemon-purple text-white'
                    : 'text-gray-400 hover:text-white hover:bg-white/10'
                }`}
              >
                <span className="text-lg">{tab.icon}</span>
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            {/* Header with Server Status */}
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-3xl font-bold text-white">
                {activeTab === 'dashboard' && 'Dashboard'}
                {activeTab === 'buy' && 'Buy Cards'}
                {activeTab === 'list' && 'Inventory'}
                {activeTab === 'history' && 'Sales History'}
              </h1>
              <ServerStatus />
            </div>
            
            {activeTab === 'dashboard' && renderDashboard()}
            {activeTab === 'buy' && renderBuy()}
            {activeTab === 'list' && renderList()}
            {activeTab === 'history' && renderHistory()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorDashboard;
